import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@mui/material/Modal';
import { useDispatch, useSelector } from 'react-redux';
import QRCode from 'qrcode';

import { selectors } from '../../LoyaltyPage/reducer';
import * as actions from '../../LoyaltyPage/actions';
import { distributeClientBonus } from '../../LoyaltyPage/helpers';

import * as Styled from '../LoyaltyPageSeparated.styles';
import type { PaymentMethod } from './LoyaltyPaymentScreen';
import JanymdaTopupModal from './JanymdaTopupModal';
import FaqTroubleshootModal, { FaqGroup } from './FaqTroubleshootModal';
import type {
  ConfirmedLoyaltyAmounts,
  StandartReceiptItem,
} from './LoyaltyApplyScreen';

interface LoyaltyProcessingScreenProps {
  paymentMethod: PaymentMethod;
  partnerPercent: number;
  isStadaBonusesCredit: boolean;
  onBack: () => void;
  variant?: 'legacy' | 'v3';
  confirmedStandartItems?: StandartReceiptItem[];
  confirmedAmounts?: ConfirmedLoyaltyAmounts | null;
  automaticCashierPayment?: boolean;
}

export const buildStandartPayload = (
  paymentType: string,
  orderNumber: string | null,
  docId: string,
  items: StandartReceiptItem[]
) => ({
  payment_type: paymentType,
  order_number: orderNumber,
  doc_id: docId,
  items,
});

/* --- иконки строк/баннеров --- */
const OrderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect
      x="6"
      y="3"
      width="12"
      height="18"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path d="M9 3v3h6V3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const ReceiptIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M9 8h6M9 12h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);
const SumIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M14 9h-3a1.5 1.5 0 000 3h2a1.5 1.5 0 010 3h-3M12 7.5v9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);
const CardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="5"
      width="18"
      height="14"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path d="M3 10h18" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const StatusDotIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </svg>
);
const CheckIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M5 12.5l4.5 4.5L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const WarnIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 3l9 16H3l9-16z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M12 10v4M12 17v.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const HourglassIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 3h12M6 21h12M7 3c0 5 10 5 10 9s-10 4-10 9M17 3c0 5-10 5-10 9s10 4 10 9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 12a9 9 0 0115.5-6.3L21 8M21 12a9 9 0 01-15.5 6.3L3 16M21 4v4h-4M3 20v-4h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const ArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M19 12H5M11 6l-6 6 6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const Chevron = ({ dir = 'down' }: { dir?: 'down' | 'right' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d={dir === 'right' ? 'M9 6l6 6-6 6' : 'M6 9l6 6 6-6'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const WalletIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="6"
      width="18"
      height="13"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M16 12h2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);
const DocIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 3h8l4 4v14H6V3z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M14 3v4h4M9 12h6M9 16h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);
const ShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const JanymdaMark = () => (
  <span
    style={{
      width: 18,
      height: 18,
      borderRadius: 5,
      background:
        'linear-gradient(135deg, #6a3df5 0%, #3b5bdb 45%, #f97316 100%)',
      display: 'inline-block',
      flexShrink: 0,
    }}
  />
);

/* --- данные FAQ --- */
type FaqLine = string | { key: string; cls: string };
interface FaqEntry {
  title: string;
  lines: FaqLine[];
}

const CASHIER_FAQ: FaqEntry[] = [
  { title: 'CASHIER_1_TITLE', lines: ['CASHIER_1_LINE1', 'CASHIER_1_LINE2'] },
  { title: 'CASHIER_2_TITLE', lines: ['CASHIER_2_LINE1', 'CASHIER_2_LINE2'] },
  { title: 'CASHIER_3_TITLE', lines: ['CASHIER_3_LINE1'] },
  { title: 'CASHIER_4_TITLE', lines: ['CASHIER_4_LINE1', 'CASHIER_4_LINE2'] },
  {
    title: 'CASHIER_5_TITLE',
    lines: [
      'CASHIER_5_LINE1',
      'CASHIER_5_LINE2',
      'CASHIER_5_LINE3',
      'CASHIER_5_LINE4',
    ],
  },
  { title: 'CASHIER_6_TITLE', lines: ['CASHIER_6_LINE1'] },
  { title: 'CASHIER_7_TITLE', lines: ['CASHIER_7_LINE1', 'CASHIER_7_LINE2'] },
  { title: 'CASHIER_8_TITLE', lines: ['CASHIER_8_LINE1', 'CASHIER_8_LINE2'] },
];

const APP_FAQ: FaqEntry[] = [
  { title: 'APP_1_TITLE', lines: ['APP_1_LINE1'] },
  {
    title: 'APP_2_TITLE',
    lines: [
      { key: 'APP_2_SUB1', cls: 'faq-sub' },
      'APP_2_LINE1',
      'APP_2_LINE2',
      'APP_2_LINE3',
      { key: 'APP_2_SUB2', cls: 'faq-sub' },
      'APP_2_LINE4',
      'APP_2_LINE5',
      'APP_2_LINE6',
      'APP_2_LINE7',
      'APP_2_LINE8',
      { key: 'APP_2_LINE9', cls: 'faq-indent' },
    ],
  },
  { title: 'APP_3_TITLE', lines: ['APP_3_LINE1', 'APP_3_LINE2'] },
  { title: 'APP_4_TITLE', lines: ['APP_4_LINE1'] },
  { title: 'APP_5_TITLE', lines: ['APP_5_LINE1', 'APP_5_LINE2'] },
  { title: 'APP_6_TITLE', lines: ['APP_6_LINE1'] },
  {
    title: 'APP_7_TITLE',
    lines: ['APP_7_LINE1', 'APP_7_LINE2', 'APP_7_LINE3', 'APP_7_LINE4'],
  },
  { title: 'APP_8_TITLE', lines: ['APP_8_LINE1'] },
  { title: 'APP_9_TITLE', lines: ['APP_9_LINE1', 'APP_9_LINE2'] },
];

/* Группировка FAQ для левой колонки модалки */
const CASHIER_FAQ_GROUPS: FaqGroup[] = [
  {
    label: 'Заказ и Standart',
    items: [CASHIER_FAQ[0], CASHIER_FAQ[1], CASHIER_FAQ[2], CASHIER_FAQ[3]],
  },
  {
    label: 'Личный кабинет',
    items: [CASHIER_FAQ[4], CASHIER_FAQ[6], CASHIER_FAQ[7]],
  },
  {
    label: 'Standart',
    items: [CASHIER_FAQ[5]],
  },
];

const APP_FAQ_GROUPS: FaqGroup[] = [
  {
    label: 'Клиент и баланс',
    items: [APP_FAQ[0], APP_FAQ[1], APP_FAQ[2]],
  },
  {
    label: 'Оплата и личный кабинет',
    items: [APP_FAQ[3], APP_FAQ[4], APP_FAQ[5], APP_FAQ[6], APP_FAQ[7]],
  },
  {
    label: 'Standart',
    items: [APP_FAQ[8]],
  },
];

const LoyaltyProcessingScreen: React.FC<LoyaltyProcessingScreenProps> = ({
  paymentMethod,
  partnerPercent,
  isStadaBonusesCredit,
  onBack,
  variant = 'legacy',
  confirmedStandartItems,
  confirmedAmounts,
  automaticCashierPayment = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const chosenReceipt = useSelector(selectors.chosenReceipt);
  const loyaltyOrderId = useSelector(selectors.loyaltyOrderId);
  const orderReceiptInfo = useSelector(selectors.orderReceiptInfo);
  const bonusUsed = useSelector(selectors.bonusUsed);
  const partnerPayments = useSelector(selectors.partnerPayments);
  const clientInfo = useSelector(selectors.clientInfo);
  const stadaInfo = useSelector(selectors.stadaInfo);
  const sendToStandartSuccess = useSelector(selectors.sendToStandartSuccess);
  const sendToStandartError = useSelector(selectors.sendToStandartError);

  const clientBalance = (clientInfo && clientInfo.balance) ?? 0;
  const stadaBalance = (stadaInfo && stadaInfo.balance) ?? 0;
  const docId = chosenReceipt?.doc_id ?? orderReceiptInfo?.doc_id ?? '';

  const [qrSrc, setQrSrc] = useState<string>('');
  const [qrError, setQrError] = useState<string>('');
  const [isQrModalOpen, setIsQrModalOpen] = useState<boolean>(false);
  const [standartRequested, setStandartRequested] = useState<boolean>(false);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState<boolean>(false);
  const [isTopupModalOpen, setIsTopupModalOpen] = useState<boolean>(false);

  const hasStadaItems = useMemo(
    () => chosenReceipt?.items?.some((item) => item.stadaPercent > 0) ?? false,
    [chosenReceipt]
  );

  const currentItems = useMemo(
    () =>
      distributeClientBonus(
        chosenReceipt?.items ?? [],
        bonusUsed ? clientBalance : 0,
        partnerPercent,
        bonusUsed && hasStadaItems && isStadaBonusesCredit ? stadaBalance : 0
      ),
    [
      chosenReceipt,
      clientBalance,
      stadaBalance,
      hasStadaItems,
      isStadaBonusesCredit,
      bonusUsed,
      partnerPercent,
    ]
  );

  const totalPrice = useMemo(
    () =>
      (chosenReceipt?.items ?? []).reduce(
        (acc, item) => acc + Number(item?.total_price ?? 0),
        0
      ),
    [chosenReceipt]
  );

  const totalDebit = useMemo(
    () => currentItems.reduce((acc, item) => acc + item.appliedBonus, 0),
    [currentItems]
  );

  const totalAmount = useMemo(() => {
    if (variant === 'v3' && confirmedAmounts) {
      return confirmedAmounts.payable;
    }
    // Display the same confirmed final prices that /scan and QR use.
    // The original receipt does not contain the applied coupon discount.
    if (variant === 'v3' && confirmedStandartItems?.length) {
      return confirmedStandartItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
    }
    const result = totalPrice - totalDebit;
    return result > 0 ? result : 0;
  }, [
    totalPrice,
    totalDebit,
    variant,
    confirmedStandartItems,
    confirmedAmounts,
  ]);

  const fmt = (n: number) =>
    Number(n || 0).toLocaleString('ru-RU', {
      maximumFractionDigits: variant === 'v3' ? 2 : 3,
    });

  // Статусы оплаты в приложении
  const isPartnerPaid = !!partnerPayments?.some((p) => p.status === 'paid');
  // Возможные негативные статусы партнёрской оплаты (при необходимости скорректировать под API)
  const isPartnerFailed = !!partnerPayments?.some((p) =>
    ['failed', 'error'].includes(String(p.status).toLowerCase())
  );
  const isPartnerPending =
    paymentMethod === 'app' && !isPartnerPaid && !isPartnerFailed;

  // Посадка в стандарт уже инициирована
  const showStandart = standartRequested;
  const standartError = sendToStandartError;
  const standartSent = !!sendToStandartSuccess && !standartError;

  // Генерация QR + посадка в стандарт
  const generateQr = async (paymentType: string) => {
    const items = distributeClientBonus(
      chosenReceipt?.items ?? [],
      bonusUsed ? clientBalance : 0,
      partnerPercent,
      bonusUsed && hasStadaItems && isStadaBonusesCredit ? stadaBalance : 0
    );
    const standartItems =
      variant === 'v3' && confirmedStandartItems?.length
        ? confirmedStandartItems
        : items.map((item) => ({
            ware_id: item.ware_id,
            price: item.unit_price - item.appliedBonus / item.part_quantity,
            quantity: item.part_quantity,
          }));

    // Preserve the existing automatic /scan contract, including the PA.
    const payload = buildStandartPayload(
      paymentType,
      loyaltyOrderId,
      docId,
      standartItems
    );
    // Coupon-only orders already have in_place payment at creation.
    // Send the confirmed PA and final prices without changing payment method.
    dispatch(actions.sendToStandart.request(payload));
    try {
      setQrError('');
      // Standard-N parses the same full, ordered payload from QR and /scan.
      const qrPayload = JSON.stringify(payload);
      const dataUrl = await QRCode.toDataURL(qrPayload, {
        errorCorrectionLevel: 'M',
        margin: 1,
        scale: 4,
      });
      setQrSrc(dataUrl);
    } catch (e: any) {
      console.error('QR generation error:', e);
      setQrSrc('');
      setQrError(e?.message || t('LoyaltySeparated.QR_ERROR'));
    }
  };

  const getPartnerOrderPayments = () => {
    dispatch(actions.getPartnerPayments.request(loyaltyOrderId));
  };

  // КАССА: сразу при входе на экран сажаем в стандарт
  useEffect(() => {
    if (paymentMethod !== 'cashier' || standartRequested) return;
    setStandartRequested(true);
    generateQr('in_place');
  }, [paymentMethod, standartRequested]);

  // ПРИЛОЖЕНИЕ: опрашиваем статус оплаты, пока не получим результат
  useEffect(() => {
    if (paymentMethod !== 'app') return;
    if (isPartnerPaid || isPartnerFailed) return;
    getPartnerOrderPayments();
    const intervalId = setInterval(getPartnerOrderPayments, 5000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, isPartnerPaid, isPartnerFailed]);

  // ПРИЛОЖЕНИЕ: оплата прошла — сажаем в стандарт
  useEffect(() => {
    if (paymentMethod !== 'app') return;
    if (isPartnerPaid && !standartRequested) {
      setStandartRequested(true);
      generateQr('internet');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, isPartnerPaid, standartRequested]);

  const handleRetryStandart = () => {
    generateQr(paymentMethod === 'app' ? 'internet' : 'in_place');
  };

  let statusTone: 'pending' | 'paid' | 'failed' = 'pending';
  let statusText = t('LoyaltySeparated.STATUS_WAITING');

  if (isPartnerPaid) {
    statusTone = 'paid';
    statusText = t('LoyaltySeparated.STATUS_PAID');
  }

  if (isPartnerFailed) {
    statusTone = 'failed';
    statusText = t('LoyaltySeparated.STATUS_FAILED');
  }

  let paymentMethodText = t('LoyaltySeparated.PAYMENT_IN_PLACE');
  if (paymentMethod === 'app') {
    paymentMethodText =
      variant === 'v3'
        ? t('LoyaltyV3.PAY_IN_BEELINE')
        : t('LoyaltySeparated.PAYMENT_IN_APP');
  }

  const faqHeaderTitle = t(
    `LoyaltySeparated.${
      paymentMethod === 'cashier'
        ? 'POTENTIAL_ISSUES'
        : 'FAQ_PAYMENT_NOT_WORKING'
    }`
  );
  const faqGroups =
    paymentMethod === 'cashier' ? CASHIER_FAQ_GROUPS : APP_FAQ_GROUPS;

  return (
    <Styled.ProcessingPage $compact={variant === 'v3'}>
      <Styled.ProcessingContent $compact={variant === 'v3'}>
        <Styled.ProcessingTitle $compact={variant === 'v3'}>
          {t('LoyaltySeparated.PROCESSING_TITLE')}
        </Styled.ProcessingTitle>

        <Styled.ProcessingLayout $compact={variant === 'v3'}>
          {/* ЛЕВАЯ КОЛОНКА */}
          <Styled.ProcessingMain>
            <Styled.ProcessingInfoCard $compact={variant === 'v3'}>
              <Styled.ProcessingInfoTitle $compact={variant === 'v3'}>
                {t('LoyaltySeparated.ORDER_INFO_TITLE')}
              </Styled.ProcessingInfoTitle>

              <Styled.ProcessingInfoRow $compact={variant === 'v3'}>
                {variant !== 'v3' && (
                  <span className="row-icon">
                    <OrderIcon />
                  </span>
                )}
                <span className="row-label">
                  {t('LoyaltySeparated.ORDER_NUMBER_LABEL')}
                </span>
                <span className="row-value">{loyaltyOrderId}</span>
              </Styled.ProcessingInfoRow>

              <Styled.ProcessingInfoRow $compact={variant === 'v3'}>
                {variant !== 'v3' && (
                  <span className="row-icon">
                    <ReceiptIcon />
                  </span>
                )}
                <span className="row-label">
                  {t('LoyaltySeparated.RECEIPT_NUMBER_LABEL')}
                </span>
                <span className="row-value">{docId}</span>
              </Styled.ProcessingInfoRow>

              <Styled.ProcessingInfoRow $compact={variant === 'v3'}>
                {variant !== 'v3' && (
                  <span className="row-icon">
                    <SumIcon />
                  </span>
                )}
                <span className="row-label">
                  {t('LoyaltySeparated.SUM_LABEL')}
                </span>
                <span className="row-value">
                  {fmt(totalAmount)} {variant === 'v3' ? '₸' : 'тг'}
                </span>
              </Styled.ProcessingInfoRow>

              <Styled.ProcessingInfoRow $compact={variant === 'v3'}>
                {variant !== 'v3' && (
                  <span className="row-icon">
                    <CardIcon />
                  </span>
                )}
                <span className="row-label">
                  {t('LoyaltySeparated.PAYMENT_METHOD_LABEL')}
                </span>
                <span className="row-value">
                  <Styled.PaymentTypePill>
                    {paymentMethod === 'app' && variant !== 'v3' ? (
                      <img
                        src="/images/janymda-logo.svg"
                        alt="janymda"
                        width="32"
                        height="32"
                      />
                    ) : null}
                    {paymentMethodText}
                  </Styled.PaymentTypePill>
                </span>
              </Styled.ProcessingInfoRow>

              {paymentMethod === 'app' && (
                <Styled.ProcessingInfoRow $compact={variant === 'v3'}>
                  {variant !== 'v3' && (
                    <span className="row-icon">
                      <StatusDotIcon />
                    </span>
                  )}
                  <span className="row-label">
                    {t('LoyaltySeparated.STATUS_LABEL')}
                  </span>
                  <span className="row-value">
                    <Styled.StatusChip tone={statusTone}>
                      {statusText}
                    </Styled.StatusChip>
                  </span>
                </Styled.ProcessingInfoRow>
              )}
            </Styled.ProcessingInfoCard>

            {/* Результат оплаты в приложении */}
            {paymentMethod === 'app' && isPartnerPending && (
              <Styled.ProcessingBanner tone="pending">
                {variant !== 'v3' && <HourglassIcon />}
                {variant === 'v3'
                  ? t('LoyaltyV3.WAITING_BEELINE_PAYMENT')
                  : t('LoyaltySeparated.WAITING_APP_PAYMENT')}
              </Styled.ProcessingBanner>
            )}

            {paymentMethod === 'app' && isPartnerFailed && (
              <Styled.ProcessingBanner tone="error">
                {variant !== 'v3' && <WarnIcon />}
                {t('LoyaltySeparated.PAYMENT_FAILED_HINT')}
              </Styled.ProcessingBanner>
            )}

            {paymentMethod === 'app' && isPartnerPaid && (
              <Styled.ProcessingBanner tone="success">
                {variant !== 'v3' && <CheckIcon />}
                {t('LoyaltySeparated.PAYMENT_SUCCESS_HINT')}
              </Styled.ProcessingBanner>
            )}

            {/* Посадка в стандарт: успех — заметный баннер */}
            {showStandart && standartSent && (
              <Styled.StandartSentBanner>
                {variant !== 'v3' && (
                  <span className="sent-icon">
                    <CheckIcon size={20} />
                  </span>
                )}
                <span className="sent-text">
                  <span className="sent-title">
                    {t('LoyaltySeparated.STANDART_SENT')}
                  </span>
                  <span className="sent-sub">
                    {t('LoyaltySeparated.STANDART_SENT_SUB')}
                  </span>
                </span>
              </Styled.StandartSentBanner>
            )}

            {/* Посадка в стандарт: ошибка */}
            {showStandart && standartError && (
              <Styled.ProcessingBanner tone="error">
                {variant !== 'v3' && <WarnIcon />}
                {t('LoyaltySeparated.STANDART_ERROR', { title: 'заказ' })}
              </Styled.ProcessingBanner>
            )}

            {/* Кнопка повторной посадки (и при успехе, и при ошибке) */}
            {showStandart && (
              <Styled.RetryStandartButton onClick={handleRetryStandart}>
                {variant !== 'v3' && <RefreshIcon />}
                {t('LoyaltySeparated.RETRY_STANDART')}
              </Styled.RetryStandartButton>
            )}

            {/* QR доступен и без локальной Уточки: его можно сканировать в Стандарт-Н. */}
            {showStandart && qrSrc && (
              <Styled.QrContainer>
                <Styled.BlockHint style={{ margin: 0, textAlign: 'center' }}>
                  {t(
                    standartError
                      ? 'LoyaltySeparated.SCAN_QR_STANDART_FALLBACK'
                      : 'LoyaltySeparated.SCAN_QR_STANDART_IF_NEEDED'
                  )}
                </Styled.BlockHint>
                <Styled.QrPreviewButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsQrModalOpen(true);
                  }}
                >
                  <img alt="QR" width={160} height={160} src={qrSrc} />
                </Styled.QrPreviewButton>
              </Styled.QrContainer>
            )}

            {qrError && <Styled.ApplyError>{qrError}</Styled.ApplyError>}

            {!automaticCashierPayment && (
              <Styled.ProcessingBackButton onClick={onBack}>
                {variant !== 'v3' && <ArrowLeft />}
                {variant === 'v3' && <span aria-hidden>←</span>}
                {t('LoyaltySeparated.BACK_TO_PAYMENT_SELECT')}
              </Styled.ProcessingBackButton>
            )}
          </Styled.ProcessingMain>

          {/* ПРАВАЯ КОЛОНКА (заглушки) */}
          {variant !== 'v3' && (
            <Styled.ProcessingSidebar>
              <Styled.SidebarDropdown
                type="button"
                onClick={() => setIsFaqModalOpen(true)}
              >
                <span className="dropdown-q">?</span>
                {faqHeaderTitle}
                <span className="dropdown-chevron">
                  <Chevron dir="right" />
                </span>
              </Styled.SidebarDropdown>

              {paymentMethod === 'app' && (
                <Styled.SidebarCard onClick={() => setIsTopupModalOpen(true)}>
                  <span className="sidebar-icon">
                    <WalletIcon />
                  </span>
                  <span className="sidebar-content">
                    <span className="sidebar-title">
                      {t('LoyaltySeparated.TOPUP_JANYMDA')}
                    </span>
                    <span className="sidebar-text">
                      {t('LoyaltySeparated.TOPUP_JANYMDA_HINT')}
                    </span>
                  </span>
                  <span className="sidebar-chevron">
                    <Chevron dir="right" />
                  </span>
                </Styled.SidebarCard>
              )}

              <Styled.SidebarCard>
                <span className="sidebar-icon">
                  <DocIcon />
                </span>
                <span className="sidebar-content">
                  <span className="sidebar-title">
                    {t('LoyaltySeparated.HOW_TO_PUNCH_RECEIPT')}
                  </span>
                  <span className="sidebar-text">
                    {paymentMethod === 'app'
                      ? t('LoyaltySeparated.HOW_TO_PUNCH_RECEIPT_APP')
                      : t('LoyaltySeparated.HOW_TO_PUNCH_RECEIPT_PLACE')}
                  </span>
                </span>
              </Styled.SidebarCard>

              <Styled.SidebarImportant>
                <span className="important-shield">
                  <ShieldIcon />
                </span>
                <span className="important-content">
                  <span className="important-title">
                    {t('LoyaltySeparated.IMPORTANT_FOR_PHARMACIST')}
                  </span>
                  <span className="important-text">
                    {t('LoyaltySeparated.IMPORTANT_FOR_PHARMACIST_TEXT')}
                  </span>
                </span>
              </Styled.SidebarImportant>
            </Styled.ProcessingSidebar>
          )}
        </Styled.ProcessingLayout>
      </Styled.ProcessingContent>

      {/* Увеличенный QR */}
      <Modal open={isQrModalOpen} onClose={() => setIsQrModalOpen(false)}>
        <Styled.QrModalContent>
          {qrSrc ? <Styled.QrBig alt="QR big" src={qrSrc} /> : null}
        </Styled.QrModalContent>
      </Modal>

      <JanymdaTopupModal
        open={isTopupModalOpen}
        onClose={() => setIsTopupModalOpen(false)}
      />

      <FaqTroubleshootModal
        open={isFaqModalOpen}
        onClose={() => setIsFaqModalOpen(false)}
        headerTitle={faqHeaderTitle}
        groups={faqGroups}
        ctaLabel={t('LoyaltySeparated.BACK_TO_PAYMENT_SELECT')}
        onCtaClick={onBack}
      />
    </Styled.ProcessingPage>
  );
};

export default LoyaltyProcessingScreen;
